<template>
  <div class="core-program-budget-justification">
    <div v-if="isLoading" class="flex justify-center items-center h-[200px]">
      <ActivityIndicator />
    </div>

    <div v-else-if="error" class="text-red-500 mb-4">
      {{ error }}
    </div>

    <div v-else class="space-y-6">
      <div v-for="(question, qIndex) in questions" :key="qIndex" class="w-full" :data-question-index="qIndex">
        <QuestionField
          v-model="answers[qIndex]"
          :label="question.label"
          :question="question.question"
          :placeholder="question.placeholder"
          :error-message="errorMessages[qIndex]"
          :required="true"
          class="budget-justification-field"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick, onUnmounted } from 'vue';
import QuestionField from '@/components/QuestionField.vue';
import ActivityIndicator from '@/components/ActivityIndicator.vue';
import { useAuthStore } from '@/stores/auth';
import { useUserProjectsStore } from '@/stores/projects';
import { useRoute } from 'vue-router';
import global from '@/global';
import { useAutoSave } from '@/composables/useAutoSave';

const props = defineProps({
  programId: {
    type: [String, Number],
    required: true
  }
});

const route = useRoute();
const authStore = useAuthStore();
const projectsStore = useUserProjectsStore();
const isLoading = ref(true);
const error = ref('');
const questions = ref([]);
const answers = ref([]);
const errorMessages = ref([]);
const lastSavedValues = ref([]);
const isDirty = ref(false);
const AUTOSAVE_DELAY = 1500; // ms

// Desired order for questions
const questionOrder = [
  "Personnel and Benefits",
  "Professional and Contractual Fees",
  "Printing and Publications",
  "Travel, Conferences, and Meetings",
  "Marketing and Communications",
  "Facilities",
  "Other Expenses",
  "In-Kind Goods & Services"
];

// Add validation function
const validateAnswers = () => {
  let isValid = true;
  errorMessages.value = questions.value.map((question, index) => {
    if (!answers.value[index] || answers.value[index].trim() === '') {
      isValid = false;
      return `${question.label} answer is required`;
    }
    return '';
  });

  // If not valid, scroll to first error
  if (!isValid) {
    nextTick(() => {
      const firstErrorIndex = errorMessages.value.findIndex(msg => msg);
      if (firstErrorIndex !== -1) {
        const errorElement = document.querySelector(`[data-question-index="${firstErrorIndex}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ 
            behavior: 'smooth',
            block: 'center'
          });
        }
      }
    });
  }

  return isValid;
};

// Watch for changes in answers
// Clear per-index error messages while typing; autosave handled by composable
watch(answers, (newAnswers, oldAnswers) => {
  isDirty.value = true;
  newAnswers.forEach((answer, index) => {
    if (answer && answer.trim() !== '' && errorMessages.value[index]) {
      errorMessages.value[index] = '';
    }
  });
}, { deep: true });

// Fetch questions
async function fetchQuestions() {
  try {
    const response = await fetch(`${global.API_URL}/questions/budget_justification`, {
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    const mappedQuestions = data.questions.map(q => ({
      id: q.id,
      label: q.label,
      question: q.question,
      placeholder: `Enter your justification for your ${q.label.toLowerCase()}. If you have no expenses in this category type "N/A" or explain why there are no expense here.`,
      category_id: q.category_id
    }));

    // Sort questions according to the specified order
    questions.value = mappedQuestions.sort((a, b) => {
      const indexA = questionOrder.indexOf(a.label);
      const indexB = questionOrder.indexOf(b.label);
      
      // If both are in the order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only A is in the order array, A comes first
      if (indexA !== -1) {
        return -1;
      }
      // If only B is in the order array, B comes first
      if (indexB !== -1) {
        return 1;
      }
      // If neither is in the order array, maintain original order
      return 0;
    });

    // Initialize arrays
    answers.value = questions.value.map(() => '');
    lastSavedValues.value = questions.value.map(() => '');
    errorMessages.value = questions.value.map(() => '');
  } catch (error) {
    console.error('Error fetching questions:', error);
    throw error;
  }
}

// Fetch answers
async function fetchAnswers() {
  try {
    await Promise.all(questions.value.map(async (question, qIndex) => {
      try {
        const res = await fetch(
          `${global.API_URL}/answers/core_program/${props.programId}?question_id=${question.id}`,
          {
            headers: {
              Authorization: `Bearer ${authStore.user.accessToken}`,
            },
          }
        );

        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }

        const data = await res.json();
        if (data.answer?.answer) {
          answers.value[qIndex] = data.answer.answer;
          lastSavedValues.value[qIndex] = data.answer.answer;
        } else {
          // If no answer exists, ensure we have empty strings
          answers.value[qIndex] = '';
          lastSavedValues.value[qIndex] = '';
        }
      } catch (error) {
        console.error(`Error fetching answer for ${question.label}:`, error);
        errorMessages.value[qIndex] = `Failed to load answer. You can still create a new one.`;
      }
    }));
  } catch (error) {
    console.error('Error fetching answers:', error);
    throw error;
  }
}

// Save answers
async function saveAnswers() {
  try {
    // Validate before saving
    if (!validateAnswers()) {
      throw new Error('Please fill in all required fields');
    }

    let allSaved = true;
    const savePromises = [];

    for (const [qIndex, question] of questions.value.entries()) {
      // Only save if there's an answer to save and it's different from last saved value
      if (answers.value[qIndex] && 
          answers.value[qIndex].trim() !== '' && 
          answers.value[qIndex] !== lastSavedValues.value[qIndex]) {
        
        const savePromise = fetch(`${global.API_URL}/answers/core_program/${props.programId}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            category_id: question.category_id,
            question_id: question.id,
            answer: answers.value[qIndex]
          }),
        }).then(async (response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const data = await response.json();
          if (data.status) {
            lastSavedValues.value[qIndex] = answers.value[qIndex];
            answers.value[qIndex] = data.data.answer;
            return true;
          } else {
            throw new Error(`Failed to save answer for ${question.label}`);
          }
        }).catch((error) => {
          console.error(`Error saving answer for ${question.label}:`, error);
          errorMessages.value[qIndex] = `Failed to save answer. Please try again.`;
          allSaved = false;
          throw error;
        });

        savePromises.push(savePromise);
      }
    }

    await Promise.all(savePromises);
    isDirty.value = false;
    return allSaved;
  } catch (error) {
    console.error('Error saving answers:', error);
    throw error;
  }
}

// Save a single answer (used by autosave). Returns true if saved.
async function saveSingleAnswer(qIndex) {
  try {
    const question = questions.value[qIndex];
    if (!question) return false;

    const answer = answers.value[qIndex];
    // Do not attempt to save empty answers or unchanged answers
    if (!answer || answer.trim() === '' || answer === lastSavedValues.value[qIndex]) {
      return false;
    }

    const response = await fetch(`${global.API_URL}/answers/core_program/${props.programId}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        category_id: question.category_id,
        question_id: question.id,
        answer: answer
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    if (data.status) {
      lastSavedValues.value[qIndex] = answer;
      // Update with server's answer text if returned
      if (data.data?.answer) {
        answers.value[qIndex] = data.data.answer;
      }
      // clear any existing error for this index
      if (errorMessages.value[qIndex]) {
        errorMessages.value[qIndex] = '';
      }
      // update dirty flag: check if any other unsaved changes remain
      isDirty.value = questions.value.some((_, idx) => answers.value[idx] !== lastSavedValues.value[idx]);
      return true;
    } else {
      throw new Error(`Failed to save answer for ${question.label}`);
    }
  } catch (error) {
    console.error(`Error autosaving answer for ${questions.value[qIndex]?.label || qIndex}:`, error);
    errorMessages.value[qIndex] = `Failed to auto-save. Please try again.`;
    // leave isDirty true so user can retry/save manually
    isDirty.value = true;
    return false;
  }
}

// Save all changed answers (used by the auto-save composable)
async function saveChangedAnswers() {
  const savePromises = [];
  for (const [qIndex, question] of questions.value.entries()) {
    if (answers.value[qIndex] && answers.value[qIndex].trim() !== '' && answers.value[qIndex] !== lastSavedValues.value[qIndex]) {
      savePromises.push(saveSingleAnswer(qIndex));
    }
  }
  await Promise.all(savePromises);
}

// Setup auto-save watcher using composable
const { setupAutoSave, isSaving: isAutoSaving, saveSuccessful, lastSavedValue, updateLastSaved } = useAutoSave(
  () => JSON.stringify(answers.value),
  saveChangedAnswers,
  () => true, // bypass per-field validation for autosave
  AUTOSAVE_DELAY
);

// Clear any timers on unmount (composable uses watch and setTimeout internally)
onUnmounted(() => {
  // no-op placeholder in case future cleanup is required
});
// Add validation function
const validate = () => {
  return validateAnswers();
};

// Initialize component
onMounted(async () => {
  try {
    isLoading.value = true;
    error.value = '';

    // Ensure core programs are loaded
    if (!projectsStore.corePrograms.length) {
      await projectsStore.fetchCorePrograms();
    }

    // Fetch questions first, then answers (sequential to ensure questions are available)
    await fetchQuestions();
    await fetchAnswers();

    // Initialize the composable's lastSavedValue to the current saved answers snapshot
    try {
      updateLastSaved(JSON.stringify(lastSavedValues.value));
    } catch (e) {
      // noop
    }

    // Start the autosave watcher after answers are loaded
    setupAutoSave(() => JSON.stringify(answers.value));
  } catch (error) {
    console.error('Error initializing component:', error);
    error.value = 'Failed to load questions and answers. Please try refreshing the page.';
  } finally {
    isLoading.value = false;
  }
});

// Expose methods to parent component
defineExpose({
  saveAnswers,
  validate
});
</script>

<style scoped>
.core-program-budget-justification {
  padding: 1rem;
}

:deep(.budget-justification-field .question-label) {
  text-align: center;
}
</style> 